import React from 'react';
import { Box } from '@mui/material';

import DataSection from '../DataSection';
import AnalyticsChartPanel from './AnalyticsChartPanel';
import AnalyticsEmptyState from './AnalyticsEmptyState';
import AnalyticsSplitLayout from './AnalyticsSplitLayout';
import AnalyticsTablePanel from './AnalyticsTablePanel';
import { WORKSPACE_GRID_MOBILE_FRAME_HEIGHTS } from './WorkspaceGridLayout';

const FLUID_CHART_MIN_FRAME_HEIGHT = { xs: 300, sm: 320, md: 240 };
const FLUID_TABLE_MIN_FRAME_HEIGHT = {
  ...WORKSPACE_GRID_MOBILE_FRAME_HEIGHTS.DASHBOARD_TABLE,
  md: 200,
};
const DEFAULT_CHART_MIN_FRAME_HEIGHT = { xs: 300, sm: 320, md: 300 };

const getPanelSizing = (section = {}) => {
  if (section.panelSizing === 'panel') {
    return {
      width: section.width || section.panelWidth || '100%',
      maxWidth: section.maxWidth || section.panelMaxWidth || '100%',
      contentWidth: section.contentWidth,
      contentMaxWidth: section.contentMaxWidth,
      contentAlign: section.contentAlign || 'stretch',
      headerLayout: section.headerLayout || 'content',
    };
  }

  return {
    width: section.panelWidth || '100%',
    maxWidth: section.panelMaxWidth || '100%',
    contentWidth: section.contentWidth || section.width,
    contentMaxWidth: section.contentMaxWidth || section.maxWidth,
    contentAlign: section.contentAlign || (section.width || section.maxWidth ? 'center' : 'stretch'),
    headerLayout: section.headerLayout || (section.width || section.maxWidth ? 'contentStart' : 'content'),
  };
};

const getTableSkeletonSpec = (table = {}) => {
  return table.skeletonSpec;
};

const getDefaultPanelSx = (fill = true) => ({
  width: '100%',
  minHeight: 0,
  height: { xs: 'auto', md: fill === false ? 'auto' : '100%' },
  maxHeight: { xs: 'none', md: fill === false ? 'none' : '100%' },
  overflow: { xs: 'visible', md: fill === false ? 'visible' : 'hidden' },
});

function WorkspaceChartTableView({
  chart,
  table,
  enabled = true,
  ratio = 'chartTable',
  sizing = 'fluidSplit',
  stackBelow = 'md',
}) {
  const chartPanelSizing = getPanelSizing(chart);
  const tablePanelSizing = getPanelSizing(table);
  const fluidSplit = sizing === 'fluidSplit';
  const chartMinFrameHeight = chart.minFrameHeight ?? (fluidSplit ? FLUID_CHART_MIN_FRAME_HEIGHT : DEFAULT_CHART_MIN_FRAME_HEIGHT);
  const tableMinFrameHeight = table.minFrameHeight ?? (fluidSplit ? FLUID_TABLE_MIN_FRAME_HEIGHT : undefined);

  return (
    <AnalyticsSplitLayout
      enabled={enabled}
      ratio={ratio}
      sizing={sizing}
      stackBelow={stackBelow}
    >
      <DataSection
        isLoading={chart.isLoading}
        skeletonSpec={chart.skeletonSpec}
        skeletonType={chart.skeletonType || 'chart'}
        minHeight={chart.minHeight ?? chartMinFrameHeight}
        fadeDelay={chart.fadeDelay || 100}
      >
        <Box sx={[getDefaultPanelSx(chart.fill ?? true), chart.outerSx]}>
          <AnalyticsChartPanel
            title={chart.title}
            description={chart.description}
            actions={chart.actions}
            exportConfig={chart.exportConfig}
            exportEnabled={chart.exportEnabled ?? chart.hasData !== false}
            variant={chart.variant || 'chart'}
            {...chartPanelSizing}
            allowAxisOverflow={chart.allowAxisOverflow}
            fill={chart.fill ?? true}
            minFrameHeight={chartMinFrameHeight}
            contentSx={chart.contentSx}
          >
            {chart.hasData ? chart.children : (
              <AnalyticsEmptyState
                icon={chart.emptyIcon}
                title={chart.emptyTitle}
                description={chart.emptyDescription}
              />
            )}
          </AnalyticsChartPanel>
        </Box>
      </DataSection>

      <DataSection
        isLoading={table.isLoading}
        skeletonSpec={getTableSkeletonSpec(table)}
        skeletonType={table.skeletonType || 'table'}
        minHeight={table.minHeight ?? FLUID_TABLE_MIN_FRAME_HEIGHT}
        fadeDelay={table.fadeDelay || 200}
      >
        <Box sx={[getDefaultPanelSx(table.fill ?? true), table.outerSx]}>
          <AnalyticsTablePanel
            title={table.title}
            description={table.description}
            actions={table.actions}
            {...tablePanelSizing}
            fill={table.fill ?? true}
            minFrameHeight={tableMinFrameHeight}
            contentSx={table.contentSx}
          >
            {table.children}
          </AnalyticsTablePanel>
        </Box>
      </DataSection>
    </AnalyticsSplitLayout>
  );
}

export default WorkspaceChartTableView;
